/**
 * Short display label for a connector row in UmlHiddenItemsDlg — a connector
 * has no caption/name of its own (see Types/UmlConnector.js), so the label is
 * built from its kind + endpoint shape names instead.
 * @param {object} connector
 * @param {Function} getShapeById - umlCanvasEngine.getShape, injected by caller
 * @returns {string}
 */
export function labelForConnector(connector, getShapeById) {
    const fromShape = getShapeById(connector.fromShapeId);
    const toShape = getShapeById(connector.toShapeId);
    const fromName = fromShape ? fromShape.caption?.text || fromShape.name || '(no name)' : '(missing)';
    const toName = toShape ? toShape.caption?.text || toShape.name || '(no name)' : '(missing)';
    return `${connector.kind || 'connector'}: ${fromName} \u2192 ${toName}`;
}

/**
 * Recursively expands one hidden top-level shape into itself + every nested
 * descendant (package children, arbitrary depth) as flat, depth-tagged rows
 * for the Hidden Items tree list. Only depth 0 is an actual member of
 * viewConfig.userHiddenItems and gets a Show action in the UI (see
 * UmlHiddenItemsRow.jsx) — descendants are shown for transparency only, since
 * they're implicitly hidden as a side effect of their hidden ancestor (see
 * resolveUserHiddenItems.js cascade). Un-hiding a single nested item on its
 * own isn't supported yet — it would still not render while its parent stays
 * hidden, so no action is offered for those rows.
 * @param {object} shape
 * @param {number} depth
 * @param {Function} labelForShapeFn - UmlWorldMapHelpers.labelForShape, injected to avoid a circular import
 * @returns {{id:string, type:'shape', kind:string, label:string, depth:number, isTopLevel:boolean}[]}
 */
export function buildShapeRows(shape, depth, labelForShapeFn) {
    const row = { id: shape.id, type: 'shape', kind: shape.kind, label: labelForShapeFn(shape), depth, isTopLevel: depth === 0 };
    const childRows = (shape.children || []).flatMap(function expandChild(child) {
        return buildShapeRows(child, depth + 1, labelForShapeFn);
    });
    return [row, ...childRows];
}
